import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { api } from "@/lib/api-client";
import {
  Button,
  Card,
  CardBody,
  Tooltip,
  cn,
  Skeleton,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Avatar,
  Tabs,
  Tab,
  RangeValue,
  DateValue,
} from "@heroui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Eye, Download, Trash2, Search } from "lucide-react";
import { filesize } from "filesize";
import { AlertButton } from "@/Components/NextBase/AlertButton";
import { LimePagination } from "@/Components/NextBase/LimePagination";
import { CustomerFileUploadForm } from "@/Components/NextBase/CustomerFileUploadForm";
import { FilePreviewModal } from "@/Components/NextBase/FilePreviewModal";
import { useState } from "react";
import { CustomerFileState, GetCustomerFiles } from "@/server-types";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeDateRangePicker } from "@/Components/NextBase/LimeDateRangePicker";
import { useDebouncedValue } from "@mantine/hooks";
import { canPreviewFile } from "@/utils/file-utils";

export const CustomerFiles = ({
  customerId,
  userAppointmentId,
  isOnAppointmentModal,
}: {
  customerId: number;
  userAppointmentId?: number;
  isOnAppointmentModal?: boolean;
}) => {
  const inputWrapperClasses = cn({
    "bg-white text-black shadow-none": userAppointmentId == null,
  });

  const {
    isOpen: isUploadModalOpen,
    onOpen: onUploadModalOpen,
    onClose: onUploadModalClose,
  } = useDisclosure();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState("10");

  const [selectedTab, setSelectedTab] = useState<"all" | "image">("all");

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchValue] = useDebouncedValue(searchQuery, 500);
  const [dateRange, setDateRange] = useState<RangeValue<DateValue> | null>(
    null,
  );

  const {
    data,
    isFetching,
    refetch,
    processedErrorMessage: fetchError,
  } = api.storage.useGetCustomerFiles({
    customerId,
    query: {
      page,
      limit: parseInt(perPage),
      type: selectedTab === "all" ? undefined : selectedTab,
      query: debouncedSearchValue,
      dateFrom:
        dateRange && dateRange.start ? dateRange.start.toString() : undefined,
      dateTo: dateRange && dateRange.end ? dateRange.end.toString() : undefined,
      userAppointmentId,
    },
  });

  return (
    <>
      <div className="mt-4">
        <LimeAlert message={fetchError} className="mb-2" />
      </div>

      <div className="mb-8 flex flex-col gap-5">
        <div className="flex items-center justify-between">
          <div className="flex w-full items-center gap-2 md:w-auto">
            <div className="w-full md:w-auto">
              <LimeInput
                placeholder={t`Iskanje`}
                startContent={<Search size={20} />}
                value={searchQuery}
                onValueChange={(value) => {
                  setSearchQuery(value);
                  setPage(1);
                }}
                classNames={{
                  inputWrapper: inputWrapperClasses,
                }}
              />
            </div>
            <div>
              <LimeDateRangePicker
                value={dateRange}
                onChange={(v) => {
                  setDateRange(v);
                  setPage(1);
                }}
                showClearButton
                isButtonOnlyMobile
                classNames={{
                  inputWrapper: inputWrapperClasses,
                }}
              />
            </div>
          </div>
          {isOnAppointmentModal ? null : (
            <div className="hidden md:block">
              <Button color="primary" size="sm" onPress={onUploadModalOpen}>
                <Trans>Naloži dokument</Trans>
              </Button>
            </div>
          )}
        </div>
        <div>
          <Tabs
            variant="light"
            classNames={{
              cursor: "bg-green-100 shadow-none",
              tabContent: "text-black",
              tab: "border-1 border-gray-100 rounded-lg",
            }}
            selectedKey={selectedTab}
            onSelectionChange={(key) => {
              setSelectedTab(key as "all" | "image");
              setPage(1);
            }}
          >
            <Tab key="all" title={t`Vsi`} />
            <Tab key="image" title={t`Galerija`} />
          </Tabs>
        </div>
      </div>

      <div className="mb-4 flex justify-end">
        <Button
          color="primary"
          size="sm"
          onPress={onUploadModalOpen}
          className={cn("block md:hidden", {
            "block!": isOnAppointmentModal,
          })}
        >
          <Trans>Naloži dokument</Trans>
        </Button>
      </div>

      <FileList
        customerId={customerId}
        isFetching={isFetching}
        perPage={perPage}
        data={data}
        userAppointmentId={userAppointmentId}
      />

      <div className="mt-4">
        <LimePagination
          paginationProps={{
            total: data?.pagination.totalPages ?? 1,
            page,
            onChange: setPage,
          }}
          perPageProps={{
            label: t`datotek`,
            values: [5, 10, 20],
            onChange: (value) => {
              setPerPage(value);
              setPage(1);
            },
            value: perPage,
          }}
        />
      </div>

      <Modal isOpen={isUploadModalOpen} onClose={onUploadModalClose}>
        <ModalContent>
          <ModalHeader>
            <Trans>Nalaganje datotek</Trans>
          </ModalHeader>
          <ModalBody>
            <CustomerFileUploadForm
              customerId={customerId}
              onSuccessfulUpload={() => {
                refetch();
              }}
              userAppointmentId={userAppointmentId}
            />
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>
    </>
  );
};

const FileList = ({
  customerId,
  isFetching,
  perPage,
  data,
  userAppointmentId,
}: {
  customerId: number;
  isFetching: boolean;
  perPage: string;
  data: GetCustomerFiles["response"] | undefined;
  userAppointmentId?: number;
}) => {
  const files = (() => {
    if (isFetching) {
      return Array.from({ length: Number(perPage) }, (_, i) => ({
        id: -1 - i,
        name: "",
        size: 0,
        state: { raw: "uploaded" as CustomerFileState, label: t`Naložena` },
        label: "",
        uploaderName: "",
        dateCreated: "",
        isSkeleton: true,
        appointment: undefined,
        customerNoteId: undefined,
      }));
    }

    if (!data) {
      return [];
    }

    return data.files.map((file) => ({
      ...file,
      isSkeleton: false,
    }));
  })();

  return (
    <>
      {isFetching || files.length !== 0 ? (
        <div
          className={cn("mt-2 grid grid-cols-1 gap-4 md:grid-cols-2", {
            "grid-cols-1 md:grid-cols-1": userAppointmentId != null,
          })}
        >
          {files.map((file) => {
            return (
              <Skeleton
                isLoaded={!file.isSkeleton}
                className="rounded-2xl"
                key={file.id}
              >
                <FileCard
                  file={file}
                  customerId={customerId}
                  userAppointmentId={userAppointmentId}
                />
              </Skeleton>
            );
          })}
        </div>
      ) : null}

      {!isFetching && files.length === 0 ? (
        <>
          <p className="text-center text-sm text-gray-500">
            <Trans>Stranka še nima datotek</Trans>
          </p>
        </>
      ) : null}
    </>
  );
};

const FileCard = ({
  file,
  customerId,
  userAppointmentId,
}: {
  file: NonNullable<GetCustomerFiles["response"]["files"]>[number];
  customerId: number;
  userAppointmentId?: number;
}) => {
  const { mutateAsync: deleteCustomerFile, isPending: isDeletingCustomerFile } =
    api.storage.useDeleteCustomerFile();

  const {
    mutateAsync: getCustomerFileDownloadPresignedUrl,
    isPending: isGettingCustomerFileDownloadPresignedUrl,
    processedErrorMessage: getCustomerFileDownloadPresignedUrlError,
  } = api.storage.useGetCustomerFileDownloadPresignedUrl();

  const handleDownloadFile = async (fileId: number) => {
    const presignedUrlData = await getCustomerFileDownloadPresignedUrl({
      id: customerId,
      fileId,
      query: {
        asAttachment: true,
      },
    });

    // Create an ephemeral <a> to force download
    const link = document.createElement("a");
    link.href = presignedUrlData.presignedUrl;

    // 'download' attribute will make the browser save the file
    // You can specify a filename here:
    link.download = presignedUrlData.fileName ?? "download";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const getPresignedUrl = async (fileId: number, asAttachment?: boolean) => {
    return await getCustomerFileDownloadPresignedUrl({
      id: customerId,
      fileId,
      query: {
        asAttachment,
      },
    });
  };

  return (
    <Card
      shadow={userAppointmentId != null ? "sm" : "none"}
      className={cn({
        "outline-2 outline-red-500 outline-solid": file.state.raw === "failed",
      })}
      classNames={{
        base: "p-6",
        body: "p-0",
        footer: "p-0",
        header: "p-0",
      }}
    >
      <CardBody>
        <div>
          <div className="mb-6 flex items-start justify-between">
            <div>
              <Button variant="flat" size="sm">
                <Trans>Datoteka</Trans>
              </Button>
            </div>
            <div className="flex items-center gap-4">
              <FilePreviewModal
                fileName={file.name}
                fileId={file.id}
                getPresignedUrl={getPresignedUrl}
                isLoading={isGettingCustomerFileDownloadPresignedUrl}
                customerId={customerId}
              >
                {({ onClick }) => (
                  <Tooltip
                    content={
                      canPreviewFile(file.name)
                        ? t`Predogled datoteke`
                        : t`Odpri datoteko`
                    }
                    delay={500}
                  >
                    <Button
                      variant="light"
                      isIconOnly
                      isLoading={isGettingCustomerFileDownloadPresignedUrl}
                      size="sm"
                      onPress={onClick}
                      aria-label={
                        canPreviewFile(file.name)
                          ? t`Predogled datoteke`
                          : t`Odpri datoteko`
                      }
                    >
                      <Eye size={24} />
                    </Button>
                  </Tooltip>
                )}
              </FilePreviewModal>

              <Tooltip content={t`Prenesi datoteko`} delay={500}>
                <Button
                  variant="light"
                  isLoading={isGettingCustomerFileDownloadPresignedUrl}
                  isIconOnly
                  size="sm"
                  onPress={async () => {
                    await handleDownloadFile(file.id);
                  }}
                  aria-label={t`Prenesi datoteko`}
                >
                  <Download size={24} />
                </Button>
              </Tooltip>
            </div>
          </div>

          <div>
            <div className="flex flex-col gap-2">
              {file.label != null && file.label.length > 0 ? (
                <p>
                  <span className="text-base leading-5 font-normal">
                    {file.label}
                  </span>
                  <span className="ml-1 text-xs leading-5 font-normal text-gray-500">
                    ({file.name})
                  </span>
                </p>
              ) : (
                <p>
                  <span className="text-base leading-5 font-normal">
                    {file.name}
                  </span>
                </p>
              )}

              <p className="text-sm font-semibold text-gray-500">
                {file.dateCreated}
              </p>

              {file.state.raw === "uploaded" && (
                <span className="text-xs font-medium text-gray-500">
                  {filesize(file.size)}
                </span>
              )}
            </div>

            {file.customerNoteId != null && (
              <p className="mt-2 text-xs font-medium text-gray-500">
                <Trans>Datoteka je povezana z zapiskom</Trans>
              </p>
            )}

            <div className="flex items-end justify-between gap-1">
              <div className="mt-4 flex items-center gap-2">
                <Avatar
                  size="sm"
                  name={file.uploaderName}
                  classNames={{
                    base: "bg-gray-200 w-8 h-8",
                  }}
                />
                <p className="text-xs font-medium text-gray-500">
                  {file.uploaderName}
                </p>
              </div>

              <AlertButton
                title={t`Izbriši datoteko`}
                content={t`Ali ste prepričani, da želite izbrisati datoteko?`}
                tooltip={t`Izbriši datoteko`}
                buttonProps={{
                  variant: "light",
                  startContent: <Trash2 size={24} />,
                  color: "danger",
                  isIconOnly: true,
                  size: "sm",
                  "aria-label": t`Izbriši datoteko`,
                }}
                confirmProps={{
                  label: t`Izbriši datoteko`,
                  onClick: async () => {
                    await deleteCustomerFile({
                      id: customerId,
                      fileId: file.id,
                    });
                  },
                  isLoading: isDeletingCustomerFile,
                }}
              ></AlertButton>
            </div>
          </div>
        </div>

        <LimeAlert message={getCustomerFileDownloadPresignedUrlError} />

        {file.state.raw !== "uploaded" && (
          <p
            className={cn("text-xs text-gray-500", {
              "font-medium text-red-500": file.state.raw === "failed",
              "text-warning-600 text-sm font-medium":
                file.state.raw === "pending",
            })}
          >
            {file.state.label}
          </p>
        )}
      </CardBody>
    </Card>
  );
};
